import { Component, ElementRef, afterNextRender, inject, input, output } from '@angular/core';

import { IconComponent } from '../../../shared/components/icon/icon.component';
import { FocusTrapDirective } from '../../../shared/directives/focus-trap/focus-trap.directive';

/**
 * The "are you sure?" in front of withdrawing one proxy's portal access.
 *
 * A dialog rather than a second pair of buttons on the row it acts on. Revoking is terminal —
 * re-granting takes a whole new proxy verification request — and when this is the proxy's last
 * active link the API disables their portal account and Entra identity with it. A prompt rendered
 * into the row says all of that in the few characters that fit beside a status chip, and it asks
 * the question in the same place the answer will appear, which is where a reflex click lands. The
 * dialog takes the click out of the list, names the caregiver, and states the consequence in a
 * sentence.
 *
 * Presentational: it asks, it does not revoke. The host owns the call because the response is the
 * patient's whole access state, which rewrites the card this dialog was opened from — and a
 * failure is reported on the row that failed, where it says which caregiver still has access.
 */
@Component({
  selector: 'app-revoke-proxy-access-modal',
  standalone: true,
  imports: [IconComponent, FocusTrapDirective],
  templateUrl: './revoke-proxy-access-modal.html',
  styleUrl: './revoke-proxy-access-modal.scss',
  host: { '(document:keydown.escape)': 'cancel()' },
})
export class RevokeProxyAccessModal {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The caregiver whose access is being withdrawn. Named in the question, not just "this link". */
  readonly proxyName = input.required<string>();

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  constructor() {
    // Focus lands on Cancel, not Revoke: the dialog exists to interrupt a reflex click, so the
    // destructive option must not be one Enter away. Same rule as the proxy approve/deny modals.
    afterNextRender(() => {
      this.elementRef.nativeElement.querySelector<HTMLElement>('.revoke-modal__cancel')?.focus();
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  confirm(): void {
    this.confirmed.emit();
  }
}
